import Combobox from "@/components/Combobox";
import AvailableFilterChip from "@/components/Filter/AvailableFilterChip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import { defaultNS, useTranslation } from "@/lib/i18n";
import type { OnChangeFn } from "@dresscode-frontend/utility-types";
import { useMemo } from "react";
import useMultiSelectAvailableFilterChipModel from "../../../../components/FilterChip/useMultiSelectAvailableFilterChipModel";
import { DeviceGenre } from "../../genre/type";
import { useDeviceGenre } from "../../genre/useDeviceGenre";
import type { DeviceGenreFilterChipProps } from "./types";

export default function DeviceGenreAvailableFilterChip({
  onChange,
}: DeviceGenreFilterChipProps) {
  const { t: tHead } = useTranslation(defaultNS, {
    keyPrefix: "component.dataTable.headers",
  });
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.filters",
  });

  const { i18n } = useDeviceGenre();

  const {
    open,
    selectedIds,
    freeText,
    onFreeTextChange,
    getOnSelectedIdsChange,
    getOnOpenChange,
  } = useMultiSelectAvailableFilterChipModel();

  const allOptions = Object.values(DeviceGenre).map((genre) => ({
    id: genre,
    label: i18n(genre),
    value: genre,
  }));

  const filteredOptions = freeText
    ? allOptions.filter((option) =>
        option.label.toLowerCase().includes(freeText.toLowerCase()),
      )
    : allOptions;

  const valueToLabelCache = useValueToLabelCache();
  const onOpenChange = useMemo(
    () =>
      getOnOpenChange({
        onFilterChange: onChange as OnChangeFn<string[] | undefined>,
      }),
    [getOnOpenChange, onChange],
  );

  const onSelectedIdsChange = useMemo(
    () => getOnSelectedIdsChange({ options: allOptions }),
    [getOnSelectedIdsChange, allOptions],
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <AvailableFilterChip showChevron iconName="DevicesIcon">
          {tHead("deviceType")}
        </AvailableFilterChip>
      </PopoverTrigger>
      <PopoverContent className="w-popover-lg p-0">
        <Combobox
          search={freeText}
          type="multiple"
          options={filteredOptions}
          value={selectedIds}
          onValueChange={onSelectedIdsChange}
          searchPlaceholder={t("deviceTypePlaceholder")}
          onSearchChange={onFreeTextChange}
          valueToLabelCache={valueToLabelCache}
        />
      </PopoverContent>
    </Popover>
  );
}
